import { Controller } from "@hotwired/stimulus"

// This controller is used to apply filters through stimulus actions.
export default class extends Controller {
  static get values() {
    return {
      url: String,
      listCharacter: { type: String, default: "|" },
      turboFrame: String,
      context: { type: String, default: "" },
    }
  }

  // This action receives a url and updates the url value for additional filtering.
  updateUrl(event) {
    if (event.detail && event.detail.url) {
      this.urlValue = event.detail.url
    } else if (event.params && event.params.url) {
      this.urlValue = event.params.url
    }
  }

  // Handle the add-filter event from leads-list--add-filter controller
  addFilter(event) {
    // If this controller has a context set AND event has a different context, ignore it
    if (
      this.contextValue &&
      event.detail &&
      event.detail.context &&
      event.detail.context !== this.contextValue &&
      event.detail.context !== ""
    ) {
      return
    }

    // Otherwise, continue with adding the filter
    if (event.detail && event.detail.name) {
      // This will show the filter UI element
      const filterElement = document.querySelector(
        `#leads-filter-${event.detail.name}, #audience-filter-${event.detail.name}`,
      )
      if (filterElement) {
        filterElement.classList.remove("d-none")
      }
    }
  }

  // This action receives a filter name and filter values and then updates the url value and reloads it.
  //
  // If the controller is defined on a turbo-frame it will reload that frame and
  // dispatch event named apply-filter:applied with the new url in the details (this can be used with the address-bar
  // controller to update the address bar if needed).
  //
  // If the controller is not defined on a turbo-frame a Turbo.visit will be performed instead.
  filter(event) {
    event.preventDefault()

    // Strictly check context - if contexts don't match, don't process this event
    if (this.contextValue && event.detail && event.detail.context) {
      if (this.contextValue !== event.detail.context) {
        return
      }
    }

    // Additional modal check - to prevent main-context controller from handling modal events
    if (
      this.contextValue === "main" &&
      event.target.closest(".modal") &&
      !event.target.closest(".modal").hasAttribute("data-ignore-context")
    ) {
      return
    }

    let url = new URL(this.urlValue, window.location.href)

    if (event.detail && event.detail.filters) {
      event.detail.filters.forEach((filter) => {
        this._updateUrlWithFilter(url, filter.name, filter.values || filter.value)
      })
    } else if (event.detail && event.detail.name) {
      this._updateUrlWithFilter(url, event.detail.name, event.detail.values || event.detail.value)
    } else if (event.target.tagName === "FORM") {
      url.searchParams.delete("offset")
      for (const pair of new FormData(event.target)) {
        url.searchParams.set(pair[0], pair[1])
      }
    } else {
      return
    }

    // Set a URL parameter to indicate that the filters have changed, this can be used by the controller if needed.
    url.searchParams.set("filters_changed", "1")

    this._load(url)
  }

  _updateUrlWithFilter(url, filter, values) {
    // Check if values is empty (undefined, empty array, or empty string)
    const isEmpty = values === undefined || (Array.isArray(values) && values.length === 0) || values === ""

    if (isEmpty) {
      // Remove the parameter completely
      url.searchParams.delete(filter)

      // Also remove the array version of the parameter if it exists
      if (url.searchParams.has(`${filter}[]`)) {
        url.searchParams.delete(`${filter}[]`)
      }

      // Also hide the filter UI element
      const filterSelector = `#leads-filter-${filter}, #audience-filter-${filter}`
      const filterElement = document.querySelector(filterSelector)
      if (filterElement) {
        filterElement.classList.add("d-none")
      }

      return
    }

    if (Array.isArray(values)) {
      values = values.join(this.listCharacterValue)
    }

    url.searchParams.set(filter, values)
    url.searchParams.delete("offset")
  }

  _load(url) {
    this.urlValue = url

    this.dispatch("loading", { detail: { url: url } })

    if (this.element.tagName === "TURBO-FRAME") {
      this.dispatch("applied", { detail: { url: url } })
      this.element.src = url
      this.element.reload()
    } else if (this.hasTurboFrameValue) {
      Turbo.visit(url, { frame: this.turboFrameValue })
    } else {
      Turbo.visit(url)
    }
  }
}
